// src/pages/FundPlanner/lib/solverGross.js
import dayjs from "lib/dayjs";
import {
  SLGS_DENOM_UNIT,
  SLGS_ROUND_MODE,
  dlog,
  dgroupCollapsed,
  dend,
  roundToDenomination,
  tenorToMonths,
  deriveMaturity,
  buildSlgsBands,
  findSlgsBandForMaturity,
  dcfLogged,
  isSlgsCertificateMaturity,
  isSlgsNoteMaturity,
} from "./solverMain";

import { buildSlgsNoteCashflows } from "./slgsNoteSchedule";

/**
 * Gross funding solver — updated for "maturity = drawDate"
 * ----------------------------------------------------------------------
 * • Draw date IS the maturity date
 * • No leadDays
 * • No maturity shifting
 * • SLGS matching still uses drawDate to find the rate bucket
 */

export function solveGrossFundingStructure(
  draws = [],
  rates = [],
  dayCount = "act/act",
  selectedCurve = "slgs",
  subscriptionDate,
  settlementDate,
  allowOptionalSkipFirstCoupon = false
) {
  dgroupCollapsed("🧮 solveGrossFundingStructure inputs");
  dlog({
    draws,
    rates,
    dayCount,
    selectedCurve,
    subscriptionDate,
    settlementDate,
    allowOptionalSkipFirstCoupon,
  });
  dend();

  const today = dayjs();
  const investments = [];
  const inflows = [];

  if (!Array.isArray(draws) || !draws.length)
    return { investments: [], inflows: [], summary: {} };
  if (!Array.isArray(rates) || !rates.length)
    return { investments: [], inflows: [], summary: {} };

  const subDateStr =
    subscriptionDate || rates[0]?.asOf || today.format("YYYY-MM-DD");
  const subDate = dayjs(subDateStr);
  const settleDate = dayjs(settlementDate || subDate);

  // ───────────────────────────────
  // Normalize market data
  // ───────────────────────────────
  const normalized = rates.map((r, idx) => {
    let maturity = r.maturity;
    if (!maturity && r.tenor) maturity = deriveMaturity(subDate, r.tenor);

    const maturityDayjs = maturity ? dayjs(maturity) : null;
    const maturityStr =
      maturityDayjs?.isValid?.() ? maturityDayjs.format("YYYY-MM-DD") : null;

    const ratePct = Number(r.value ?? r.ratePct ?? 0);

    const isSLGS =
      selectedCurve?.toLowerCase() === "slgs" ||
      r.ticker?.toUpperCase?.().startsWith("SLGA") ||
      r.code?.toUpperCase?.().startsWith("SLGA");

    const months =
      typeof r.months === "number"
        ? r.months
        : r.tenor
        ? tenorToMonths(r.tenor)
        : null;

    const type = isSLGS
      ? "SLGS"
      : r.source?.toLowerCase?.().includes("treasury")
      ? "US Treasury"
      : "Other";

    let name;
    if (isSLGS) {
      if (maturityStr && isSlgsCertificateMaturity(maturityStr, settleDate)) {
        name = `SLGS Certificate ${r.tenor || ""}`.trim();
      } else if (maturityStr && isSlgsNoteMaturity(maturityStr, settleDate)) {
        name = `SLGS Note ${r.tenor || ""}`.trim();
      } else {
        name = `SLGS Time Deposit ${r.tenor || ""}`.trim();
      }
    } else {
      name = r.name?.trim() || r.asset_class || r.security_id || "Security";
    }

    return {
      ...r,
      maturity: maturityStr,
      ratePct,
      type,
      name,
      months,
      price: 1.0,
      _rowIndex: idx,
    };
  });

  const findNearestByMonths = (targetMonths) => {
    if (!normalized.some((n) => typeof n.months === "number"))
      return normalized[0];

    return normalized.reduce((closest, r) => {
      if (typeof r.months !== "number") return closest;
      const diff = Math.abs(r.months - targetMonths);
      const bestDiff = Math.abs((closest.months ?? 99999) - targetMonths);
      return diff < bestDiff ? r : closest;
    }, normalized[0]);
  };

  const slgsRowsSorted =
    selectedCurve?.toLowerCase() === "slgs"
      ? normalized
          .filter((n) => n.type === "SLGS")
          .sort((a, b) => (a.months ?? 999999) - (b.months ?? 999999))
      : [];

  const slgsBands =
    selectedCurve?.toLowerCase() === "slgs"
      ? buildSlgsBands(settleDate, slgsRowsSorted)
      : [];

  dgroupCollapsed("📈 Normalized market rows (first 6 shown)");
  dlog(slgsRowsSorted.slice(0, 6));
  dend();

  // ───────────────────────────────
  // MAIN LOOP — maturity = drawDate
  // ───────────────────────────────
  const slgsIndexes = [];
  let totalRequested = 0;

  for (const [i, draw] of draws.entries()) {
    dgroupCollapsed(`➡️ Draw #${i + 1}`);

    const drawDate = dayjs(draw.date);
    const maturityDate = drawDate;
    const requestedPrincipal = Number(draw.amount);

    if (!requestedPrincipal || !drawDate.isValid()) {
      dlog("Skipping invalid draw", draw);
      dend();
      continue;
    }

    totalRequested += requestedPrincipal;

    let match;
    if (selectedCurve?.toLowerCase() === "slgs" && slgsBands.length) {
      match = findSlgsBandForMaturity(maturityDate, slgsBands);
    } else {
      const tenorMonths = maturityDate.diff(subDate, "month");
      match = findNearestByMonths(tenorMonths);
    }

    if (!match) {
      dlog("No market match found", {
        draw,
        maturityDate: maturityDate.toISOString(),
      });
      dend();
      continue;
    }

    const ratePct = Number(match.ratePct);
    const rate = ratePct / 100;

    const isSLGS = match.type === "SLGS";
    const isNote =
      isSLGS &&
      isSlgsNoteMaturity(
        maturityDate.format("YYYY-MM-DD"),
        settleDate.format("YYYY-MM-DD")
      );

    const securityName =
      isSLGS && isNote
        ? `SLGS Note ${match.tenor || ""}`.trim()
        : isSLGS
        ? `SLGS Certificate ${match.tenor || ""}`.trim()
        : match.name;

    const accrualBasisForSLGS = isNote ? "act/act-ust" : "act/act";

    const principal = isSLGS
      ? roundToDenomination(
          requestedPrincipal,
          SLGS_DENOM_UNIT,
          SLGS_ROUND_MODE
        )
      : requestedPrincipal;

    const cashInflowsForThis = [];
    let totalInterest = 0;
    let firstInterestPaymentDate = null;

    const r2 = (x) => Math.round((Number(x) + Number.EPSILON) * 100) / 100;

    // ───────────────────────────────
    // SLGS Note path (coupon schedule)
    // ───────────────────────────────
    if (isNote) {
      const {
        regular,
        firstInterestPaymentDate: regularFirstInterestPaymentDate,
      } = buildSlgsNoteCashflows({
        settlement: settleDate.format("YYYY-MM-DD"),
        maturity: maturityDate.format("YYYY-MM-DD"),
        principal,
        ratePct,
        descriptionBase: securityName,
        allowSkipFirstCoupon: allowOptionalSkipFirstCoupon,
      });

      firstInterestPaymentDate = regularFirstInterestPaymentDate || null;

      for (const row of regular) {
        const cd = dayjs(row.date);
        const isFinal = row.includesPrincipal || cd.isSame(maturityDate, "day");
        const flowAmount = Number(row.amount) || 0;

        const interestPortion = isFinal
          ? r2(flowAmount - principal)
          : r2(flowAmount);

        cashInflowsForThis.push({
          invId: `INV-${i + 1}`,
          date: cd.format("YYYY-MM-DD"),
          type: "Inflow",
          description: `${isFinal ? "SLGS maturity" : "SLGS interest"} — ${
            securityName
          }`,
          inflow: r2(flowAmount),
          outflow: 0,
          includesPrincipal: isFinal,
        });

        totalInterest = r2(totalInterest + interestPortion);
      }
    } else {
      // ───────────────────────────────
      // Certificates & non-SLGS
      // Simple interest: settleDate → drawDate
      // ───────────────────────────────
      const basis = isSLGS ? accrualBasisForSLGS : dayCount;
      const dcf = dcfLogged(
        settleDate.format("YYYY-MM-DD"),
        maturityDate.format("YYYY-MM-DD"),
        basis
      );
      const interest = r2(principal * rate * dcf);
      totalInterest = interest;

      cashInflowsForThis.push({
        invId: `INV-${i + 1}`,
        date: maturityDate.format("YYYY-MM-DD"),
        type: "Inflow",
        description: `${match.type} maturity — ${securityName}`,
        inflow: r2(principal + interest),
        outflow: 0,
        includesPrincipal: true,
      });
    }

    const investmentRow = {
      id: `INV-${i + 1}`,
      security_id: match.security_id,
      name: securityName,
      tenor: match.tenor,
      source: match.source,
      asset_class: match.asset_class,
      type: match.type,
      maturityDate: maturityDate.format("YYYY-MM-DD"),
      firstInterestPaymentDate: isNote ? firstInterestPaymentDate : null,
      drawDate: drawDate.format("YYYY-MM-DD"),
      subscriptionDate: subDateStr,
      settlementDate: settleDate.format("YYYY-MM-DD"),
      tenorMonths: tenorToMonths(match.tenor),
      ratePct,
      rate,
      price: 100,
      principal,
      cost: principal,
      totalInterest,
      denominationUnit: isSLGS ? SLGS_DENOM_UNIT : null,
      denominationMode: isSLGS ? SLGS_ROUND_MODE : null,
      requestedAmount: requestedPrincipal,
    };

    investments.push(investmentRow);
    inflows.push(...cashInflowsForThis);
    if (isSLGS) slgsIndexes.push(i);

    dend();
  }

  // ───────────────────────────────
  // Post-pass SLGS "adjust last"
  // ───────────────────────────────
  if (slgsIndexes.length > 0) {
    const totalRounded = investments
      .filter((r) => r.type === "SLGS")
      .reduce((sum, r) => sum + r.principal, 0);

    const delta = totalRequested - totalRounded;
    const adj = Math.round(delta / SLGS_DENOM_UNIT) * SLGS_DENOM_UNIT;

    if (adj !== 0) {
      const lastIdx = slgsIndexes[slgsIndexes.length - 1];
      const inv = investments[lastIdx];
      const oldP = inv.principal;
      const newP = Math.max(0, oldP + adj);

      if (newP !== oldP) {
        const ratio = oldP > 0 ? newP / oldP : 0;
        inv.principal = newP;
        inv.cost = newP;
        inv.totalInterest = Math.round(inv.totalInterest * ratio * 100) / 100;

        for (const cf of inflows) {
          if (cf.invId === inv.id) {
            if (cf.includesPrincipal) {
              const oldInterest = cf.inflow - oldP;
              cf.inflow = newP + Math.round(oldInterest * ratio * 100) / 100;
            } else {
              cf.inflow = Math.round(cf.inflow * ratio * 100) / 100;
            }
          }
        }
      }
    }
  }

  // ───────────────────────────────
  // Summary
  // ───────────────────────────────
  const totalPrincipal = investments.reduce(
    (s, r) => s + (r.principal || 0),
    0
  );
  const totalCost = investments.reduce((s, r) => s + (r.cost || 0), 0);
  const avgRate =
    investments.length > 0
      ? investments.reduce((s, r) => s + (r.ratePct || 0), 0) /
        investments.length
      : null;

  const summary = {
    totalPrincipal,
    totalCost,
    avgRate,
    count: investments.length,
  };

  dgroupCollapsed("✅ Solver result");
  dlog({ summary, investments, inflows });
  dend();

  return { investments, inflows, summary };
}